import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Col,
  Form,
  Modal,
  Row,
  Select,
  Space,
  Typography,
  message,
} from "antd";
import { EditOutlined } from "@ant-design/icons";
import { Booking } from "../../../types/booking";
import { PaymentTerm } from "../../../types/invoice";
import { getPaymentTermsForDisplay } from "../../../utils/paymentTermsUtils";
import { supabase } from "../../../lib/supabase";

const { Text } = Typography;

export interface PricingPaymentTerm {
  id: number;
  days: number;
  interest_vat_import_duties: number;
  created_at?: string;
}

interface PaymentTermsSectionProps {
  selectedBooking: Booking | null;
  paymentTerm: PaymentTerm;
  customPaymentTerms: string;
  /** After company.pricing_payment_term is saved (full term row, or null if cleared). */
  onCompanyPaymentTermUpdated?: (
    term: PricingPaymentTerm | null,
  ) => void | Promise<void>;
}

function getCompany(booking: Booking) {
  return Array.isArray(booking.company_info)
    ? booking.company_info[0]
    : booking.company_info;
}

function getCompanyPaymentTermId(booking: Booking): number | undefined {
  const company = getCompany(booking);
  const raw = company?.pricing_payment_term as unknown;
  if (typeof raw === "number" && raw > 0) return raw;
  if (Array.isArray(raw) && raw[0] && typeof raw[0] === "object") {
    const id = Number((raw[0] as { id?: number }).id);
    return Number.isFinite(id) && id > 0 ? id : undefined;
  }
  if (raw && typeof raw === "object") {
    const id = Number((raw as { id?: number }).id);
    return Number.isFinite(id) && id > 0 ? id : undefined;
  }
  return undefined;
}

function isPricingPaymentTerm(term: unknown): term is PricingPaymentTerm {
  return Boolean(
    term &&
      typeof term === "object" &&
      term !== null &&
      "interest_vat_import_duties" in term &&
      "days" in term,
  );
}

function getCompanyInterestRate(booking: Booking): number {
  const company = getCompany(booking);
  const paymentTerm = company?.pricing_payment_term as unknown;
  if (Array.isArray(paymentTerm) && paymentTerm.length > 0) {
    const first = paymentTerm[0];
    return isPricingPaymentTerm(first) ? first.interest_vat_import_duties : 0;
  }
  if (isPricingPaymentTerm(paymentTerm)) {
    return paymentTerm.interest_vat_import_duties;
  }
  return 0;
}

const PaymentTermsSection: React.FC<PaymentTermsSectionProps> = ({
  selectedBooking,
  paymentTerm,
  customPaymentTerms,
  onCompanyPaymentTermUpdated,
}) => {
  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [catalog, setCatalog] = useState<PricingPaymentTerm[]>([]);
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [draftTermId, setDraftTermId] = useState<number | null>(null);

  const company = selectedBooking ? getCompany(selectedBooking) : null;
  const companyId = company?.id;
  const companyName = company?.name;

  useEffect(() => {
    if (!editOpen) return;
    let cancelled = false;
    (async () => {
      setLoadingCatalog(true);
      try {
        const { data, error } = await supabase
          .from("pricing_payment_terms")
          .select("id, days, interest_vat_import_duties, created_at")
          .order("days", { ascending: true });
        if (error) throw error;
        if (!cancelled) {
          setCatalog((data as PricingPaymentTerm[]) || []);
          setDraftTermId(
            selectedBooking
              ? getCompanyPaymentTermId(selectedBooking) ?? null
              : null,
          );
        }
      } catch (err) {
        console.error(err);
        message.error("Failed to load payment terms");
      } finally {
        if (!cancelled) setLoadingCatalog(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [editOpen, selectedBooking]);

  const mockInvoice = useMemo(
    () => ({
      payment_term: paymentTerm,
      custom_payment_terms: customPaymentTerms,
      booking: selectedBooking
        ? { company_info: selectedBooking.company_info }
        : undefined,
    }),
    [paymentTerm, customPaymentTerms, selectedBooking],
  );

  if (!selectedBooking) {
    return null;
  }

  const paymentTerms = getPaymentTermsForDisplay(
    mockInvoice as Parameters<typeof getPaymentTermsForDisplay>[0],
  );

  const sourceIcons = {
    custom: "🎯",
    invoice: "📋",
    company: "🏢",
  };

  const sourceColors = {
    custom: "#722ed1",
    invoice: "#1890ff",
    company: "#52c41a",
  };

  const interestRate = getCompanyInterestRate(selectedBooking);

  const saveCompanyTerm = async () => {
    if (!companyId) {
      message.error("No company on this booking");
      return;
    }
    setSaving(true);
    try {
      const { error } = await supabase
        .from("company")
        .update({ pricing_payment_term: draftTermId })
        .eq("id", companyId);
      if (error) throw error;

      const selected =
        draftTermId == null
          ? null
          : catalog.find((term) => term.id === draftTermId) || null;

      await onCompanyPaymentTermUpdated?.(selected);
      message.success(
        selected
          ? `Payment terms saved for ${companyName || "company"}`
          : `Payment terms cleared — invoices default to Net 30 days`,
      );
      setEditOpen(false);
    } catch (err) {
      console.error(err);
      message.error(
        err instanceof Error ? err.message : "Failed to save payment terms",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Row gutter={8}>
        <Col span={24}>
          <Form.Item
            label={
              <Space size={8}>
                <span>Payment Terms (for Moneybird)</span>
                {companyId && (
                  <Button
                    type="link"
                    size="small"
                    icon={<EditOutlined />}
                    style={{ padding: 0, height: "auto" }}
                    onClick={() => setEditOpen(true)}
                  >
                    Edit for company
                  </Button>
                )}
              </Space>
            }
            style={{ marginBottom: 8 }}
          >
            <div
              style={{
                border: "1px solid #d9d9d9",
                borderRadius: "6px",
                padding: "8px 12px",
                backgroundColor: "#fafafa",
              }}
            >
              <div
                style={{
                  fontSize: "14px",
                  fontWeight: "bold",
                  color:
                    sourceColors[
                      paymentTerms.source as keyof typeof sourceColors
                    ],
                  marginBottom: "4px",
                }}
              >
                {sourceIcons[paymentTerms.source as keyof typeof sourceIcons]}{" "}
                {paymentTerms.fullDisplay}
              </div>
              <div style={{ fontSize: "12px", color: "#666" }}>
                {paymentTerms.source === "custom"
                  ? "🎯 Using custom payment terms"
                  : paymentTerms.source === "invoice"
                    ? "📋 Using invoice-specific payment terms"
                    : "🏢 Using company default payment terms"}
              </div>
              <div
                style={{
                  fontSize: "12px",
                  color: "#fa8c16",
                  marginTop: "2px",
                }}
              >
                📅 Due Date: {paymentTerms.dueDate}
              </div>
              {interestRate > 0 && (
                <div
                  style={{
                    fontSize: "12px",
                    color: "#fa8c16",
                    marginTop: "2px",
                  }}
                >
                  💰 Interest Rate: {interestRate}% (on import VAT and duties)
                </div>
              )}
              {!getCompanyPaymentTermId(selectedBooking) && (
                <div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>
                  Company has no payment terms set — using Net 30 default.
                </div>
              )}
            </div>
          </Form.Item>
        </Col>
      </Row>

      <Modal
        title={`Payment terms — ${companyName || "Company"}`}
        open={editOpen}
        onCancel={() => setEditOpen(false)}
        onOk={() => void saveCompanyTerm()}
        okText="Save on company"
        confirmLoading={saving}
        destroyOnClose
      >
        <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
          Saved on the company profile. Future invoices for this company use
          these terms. Clear to fall back to Net 30 days.
        </Text>
        <Select
          allowClear
          showSearch
          loading={loadingCatalog}
          placeholder="Select payment terms (default: Net 30)"
          style={{ width: "100%" }}
          value={draftTermId ?? undefined}
          onChange={(value) => setDraftTermId(value ?? null)}
          optionFilterProp="label"
          options={catalog.map((term) => ({
            value: term.id,
            label: `${term.days} day${term.days === 1 ? "" : "s"}, ${term.interest_vat_import_duties}% interest`,
          }))}
        />
      </Modal>
    </>
  );
};

export default PaymentTermsSection;
